import React, { useLayoutEffect, useRef, useState } from 'react';

import { Inner, Outer } from './styles';

type ResizeObserverCtor = new (callback: () => void) => {
  observe(target: Element): void;
  disconnect(): void;
};

/**
 * Anima a altura do conteúdo quando ela muda — a troca Lista ⇄ Gráfico, e o gráfico
 * que chega depois do spinner.
 *
 * Mede o conteúdo com `ResizeObserver` em vez de fixar alturas: a lista tem a altura
 * das leituras que tem, e o gráfico, a do que desenhou. ⓘ O TS 3.9 do projeto ainda
 * não tipa o `ResizeObserver`, daí o construtor lido de `window` com tipo mínimo; sem
 * ele (navegador antigo) a altura só não anima.
 */
const AnimatedHeight: React.FC = ({ children }) => {
  const innerRef = useRef<HTMLDivElement>(null);
  const [height, setHeight] = useState<number>();

  useLayoutEffect(() => {
    const inner = innerRef.current;
    const Observer = (
      window as unknown as {
        ResizeObserver?: ResizeObserverCtor;
      }
    ).ResizeObserver;

    if (!inner || !Observer) {
      return undefined;
    }

    const measure = () => setHeight(inner.offsetHeight);
    const observer = new Observer(measure);

    measure();
    observer.observe(inner);

    return () => observer.disconnect();
  }, []);

  // ⚠️ Medição síncrona a cada renderização, além do observer: o navegador não
  // entrega callbacks de `ResizeObserver` a uma aba que não está pintando quadros
  // (em segundo plano, encoberta), e a altura ficava presa na do gráfico depois de
  // voltar para a lista. A troca Lista ⇄ Gráfico re-renderiza este componente, então
  // ela fica garantida; o observer cobre o que muda por dentro (o gráfico que chega).
  useLayoutEffect(() => {
    const measured = innerRef.current?.offsetHeight;

    if (measured !== undefined && measured !== height) {
      setHeight(measured);
    }
  }, [children, height]);

  return (
    <Outer style={height === undefined ? undefined : { height }}>
      <Inner ref={innerRef}>{children}</Inner>
    </Outer>
  );
};

export default AnimatedHeight;
